// Feature Tags Constants
// Based on the product features spreadsheet

export const FeatureTags = {
  // Charts
  CHARTS: 'FEATURE_CHARTS',
  STANDARD_INDICATORS: 'FEATURE_STANDARD_INDICATORS',
  DTU_INDICATORS: 'FEATURE_DTU_INDICATORS',
  CUSTOM_INDICATORS: 'FEATURE_CUSTOM_INDICATORS',

  // Backtests
  SHOWCASE: 'FEATURE_SHOWCASE',
  BACKTEST_SETTINGS: 'FEATURE_BACKTEST_SETTINGS',
  BACKTEST_INSIGHTS: 'FEATURE_BACKTEST_INSIGHTS',
  FEATURED_BACKTESTS: 'FEATURE_FEATURED_BACKTESTS',
  CONDITIONAL_RATIOS: 'FEATURE_CONDITIONAL_RATIOS',

  // Pattern Builder
  PATTERN_BUILDER: 'FEATURE_PATTERN_BUILDER',
  PRIVATE_PATTERNS: 'FEATURE_PRIVATE_PATTERNS',

  // Alerts
  ALERTS: 'FEATURE_ALERTS',
  ALERT_CREATE: 'FEATURE_ALERT_CREATE',
  RESTRICTED_ALERT_TIMEFRAMES: 'FEATURE_RESTRICTED_ALERT_TIMEFRAMES',
  AUTOTRADING: 'FEATURE_AUTOTRADING',
  MANAGE_TRADING_ACCOUNTS: 'FEATURE_MANAGE_TRADING_ACCOUNTS',

  // Global
  CAN_TOGGLE_TRADING_HOUSE: 'FEATURE_CAN_TOGGLE_TRADING_HOUSE',

  // Product Identifiers
  TPA_AFFILIATE: 'TPA_AFFILIATE',
  TRADER_PLUS: 'TRADER_PLUS',
  STARTER: 'STARTER',
  STARTER_PLUS: 'STARTER_PLUS',
  PRO: 'PRO',
  PRO_PLUS: 'PRO_PLUS',
  ADMIN_ACCESS_ALERTS: 'ADMIN_ACCESS_ALERTS',
} as const

export type FeatureTag = (typeof FeatureTags)[keyof typeof FeatureTags]

export const ALL_FEATURE_TAGS: FeatureTag[] = Object.values(FeatureTags)

// Product identifier tags — assigned per-product as a productTag, not as feature tags/denyTags
export const PRODUCT_TAGS: FeatureTag[] = [
  FeatureTags.TPA_AFFILIATE,
  FeatureTags.TRADER_PLUS,
  FeatureTags.STARTER,
  FeatureTags.STARTER_PLUS,
  FeatureTags.PRO,
  FeatureTags.PRO_PLUS,
  FeatureTags.ADMIN_ACCESS_ALERTS,
]

// Feature tags grouped by category for UI display (excludes product identifier tags)
export const FEATURE_TAG_CATEGORIES: { name: string; tags: FeatureTag[] }[] = [
  {
    name: 'Charts',
    tags: [FeatureTags.CHARTS, FeatureTags.STANDARD_INDICATORS, FeatureTags.DTU_INDICATORS],
  },
  {
    name: 'Backtests',
    tags: [
      FeatureTags.SHOWCASE,
      FeatureTags.BACKTEST_SETTINGS,
      FeatureTags.BACKTEST_INSIGHTS,
      FeatureTags.FEATURED_BACKTESTS,
      FeatureTags.CONDITIONAL_RATIOS,
    ],
  },
  {
    name: 'Pattern Builder',
    tags: [
      FeatureTags.PATTERN_BUILDER,
      FeatureTags.PRIVATE_PATTERNS,
      FeatureTags.CUSTOM_INDICATORS,
    ],
  },
  {
    name: 'Alerts',
    tags: [
      FeatureTags.ALERTS,
      FeatureTags.ALERT_CREATE,
      FeatureTags.RESTRICTED_ALERT_TIMEFRAMES,
      FeatureTags.CONDITIONAL_RATIOS,
    ],
  },
  {
    name: 'Global',
    tags: [FeatureTags.CAN_TOGGLE_TRADING_HOUSE],
  },
]

// Human-readable labels for feature tags
export const FEATURE_TAG_LABELS: Record<FeatureTag, string> = {
  [FeatureTags.CHARTS]: 'Charts',
  [FeatureTags.STANDARD_INDICATORS]: 'Standard Indicators',
  [FeatureTags.DTU_INDICATORS]: 'DTU Indicators',
  [FeatureTags.CUSTOM_INDICATORS]: 'Custom Indicators',
  [FeatureTags.SHOWCASE]: 'Showcase',
  [FeatureTags.BACKTEST_SETTINGS]: 'Backtest Settings',
  [FeatureTags.BACKTEST_INSIGHTS]: 'Backtest Insights',
  [FeatureTags.FEATURED_BACKTESTS]: 'Featured Backtests',
  [FeatureTags.CONDITIONAL_RATIOS]: 'Conditional Ratios',
  [FeatureTags.PATTERN_BUILDER]: 'Pattern Builder',
  [FeatureTags.PRIVATE_PATTERNS]: 'Private Patterns',
  [FeatureTags.ALERTS]: 'Alerts',
  [FeatureTags.ALERT_CREATE]: 'Alert Create',
  [FeatureTags.RESTRICTED_ALERT_TIMEFRAMES]: 'Restricted Alert Timeframes',
  [FeatureTags.AUTOTRADING]: 'Auto Trading',
  [FeatureTags.MANAGE_TRADING_ACCOUNTS]: 'Manage Trading Accounts',
  [FeatureTags.CAN_TOGGLE_TRADING_HOUSE]: 'Can Toggle Trading House',
  [FeatureTags.TPA_AFFILIATE]: 'TPA Affiliate',
  [FeatureTags.TRADER_PLUS]: 'Trader Plus',
  [FeatureTags.STARTER]: 'Starter',
  [FeatureTags.STARTER_PLUS]: 'Starter Plus',
  [FeatureTags.PRO]: 'Pro',
  [FeatureTags.PRO_PLUS]: 'Pro Plus',
  [FeatureTags.ADMIN_ACCESS_ALERTS]: 'Admin Access Alerts',
}
